import { BasicLayout, OrientationMap, Orientor } from '@1x2network/layout'
import { BezierCurve, ParticleTrail } from '@1x2network/particle_trail'
import {
    applySpecification,
    ContainerSpecification,
    createSpine,
    Specification,
    Spine,
    SpineSpecification,
    ValueText,
    ValueTextSpecification,
} from '@1x2network/pixi_assets'
import { SplashComponent } from '@1x2network/pixi_splash'
import gsap from 'gsap'
import { Container, IDestroyOptions } from 'pixi.js'
import { formatPixiText } from '../../../utils/formatCurrency'
import { makeShape } from '../../../utils/shapes/makeShape'
import { CollectionSpecification } from '../../Collection'

/**
 * A SplashComponent that shows a preview of the Collector feature on repeat
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class CollectorAnimationLoop extends SplashComponent<CollectorAnimationLoopSpecification> {
    private collector: CashSpine
    private cash: CashSpine[]
    private timelines: GSAPTimeline[] = []
    private particleTrails: ParticleTrail[] = []
    private orientor: Orientor<ContainerSpecification>

    constructor(options: { parent?: Container; layout: BasicLayout; spec: CollectorAnimationLoopSpecification }) {
        super(options)

        this.collector = this.createCash(this.specification.collector)
        this.cash = this.specification.cash.map((spec) => this.createCash(spec))
        this.collector.text.visible = false

        this.orientor = new Orientor({
            layout: options.layout,
            property: this.specification,
            onOrient: (active) => {
                applySpecification(this, active)
            },
        })
    }

    private createCash(cashSpec: CashSpineSpecification): CashSpine {
        const { value = 0 } = cashSpec
        const spine = createSpine({ spec: cashSpec, parent: this })
        const text = new ValueText({
            parent: spine,
            spec: { ...this.specification.cashText },
            format: formatPixiText,
        })
        text.value = value
        return { spine, value, text }
    }

    public show(): void {
        super.show()
        this.animate()
    }

    private animate(): void {
        const timeline = gsap.timeline({ repeat: -1 })
        const collectorSpec = Specification.get('collection') as CollectionSpecification
        const { trailAnim, trailSpec } = collectorSpec
        const shape = makeShape(trailAnim.shape.kind, trailAnim.shape.options)
        const { spine: collectorSpine, text: collectorText } = this.collector
        let collectorValue = 0

        for (let i = 0; i < this.cash.length; i++) {
            const cash = this.cash[i]
            const { spine: cashSpine, value: cashValue, text: cashText } = cash
            const points = shape.calculateControlPoints(cashSpine.position, collectorSpine.position)
            const bezier = new BezierCurve(points, trailAnim.steps)
            const particleTrail = new ParticleTrail(bezier, trailSpec)
            this.addChild(particleTrail)

            timeline
                .call(
                    () => {
                        cashSpine.play('win')
                    },
                    [],
                    '+=0.6',
                )
                .to(cashText.asset, { pixi: { tint: 0x7f7f7f } }, '+=0.35')
                .add(particleTrail.animate(trailAnim.tweenVars), '<')
                .to(collectorSpine.scale, { x: 0.97, y: 0.97, duration: 0.05, repeat: 1, yoyo: true }, '>-0.05')
                .call(
                    () => {
                        collectorValue += cashValue
                        collectorText.value = collectorValue
                    },
                    [],
                    '-=0.2',
                )

            if (i === 0) {
                timeline.set(collectorText, { visible: true, alpha: 1 }, '<')
            }

            // Reset
            if (i === this.cash.length - 1) {
                const texts = this.cash.map(({ text }) => text.asset)
                timeline
                    .to(collectorText, { alpha: 0 }, '+=0.85')
                    .to(texts, { pixi: { tint: 0xffffff } }, '<')
                    .call(() => {
                        collectorValue = 0
                    })
            }

            // Store timeline and particle trail for destruction
            this.timelines.push(timeline)
            this.particleTrails.push(particleTrail)
        }
    }

    public hide(): void {
        super.hide()

        this.timelines.forEach((timeline) => timeline.kill())
        this.timelines.length = 0
        this.particleTrails.forEach((trail) => trail.destroy())
        this.particleTrails.length = 0
    }

    public destroy(options?: IDestroyOptions | boolean): void {
        this.orientor.disconnect()
        super.destroy(options)
    }
}

type CashSpine = { spine: Spine; value: number; text: ValueText }
type CashSpineSpecification = SpineSpecification & { value?: number }
type ContainerSpecWithOrientation = ContainerSpecification & OrientationMap<ContainerSpecification>

export interface CollectorAnimationLoopSpecification extends ContainerSpecWithOrientation {
    cashText: ValueTextSpecification
    collector: SpineSpecification
    cash: CashSpineSpecification[]
}
